<template>
  <div class="app-create-itinerary-modal">
    <div class="itinerary-modal-dialog">
      <div class="itinerary-modal-content">
        <button class="itinerary-modal-close" type="button" @click="close">
          <TheCrossIcon />
        </button>

        <section v-if="!finishCreate" class="form-section">
          <header class="form-section__header">
            <h3 class="form-section__title">Create Itinerary</h3>
          </header>

          <form class="modal-form" @submit.prevent="handleSubmit">
            <CreateItineraryFormInput
              v-model="form.title"
              :error="errors.title"
              label="Title"
              placeholder="Enter title"
              required
              @input="handleInput('title', $event)"
            />
            <CreateItineraryFormSelect
              v-model="form.destination"
              :error="errors.destination"
              label="Destination"
              :options="destinationsForSelectList"
              placeholder="Choose destination"
              required
            />
            <AppFormDatePicker
              v-model="form.dates"
              :error="errors.dates"
              label="Dates of Trip"
              name="datesOfTrip"
              placeholder="Choose dates"
              range
              required
              type="date"
            />
            <CreateItineraryFormTextarea
              v-model="form.description"
              :error="errors.description"
              label="Summary"
              placeholder="Enter summary"
              @input="handleInput('description', $event)"
              @submit="handleSubmit"
            />
            <button class="modal-form__btn-submit" :disabled="isFormSubmitDisabled" type="submit">Create</button>
          </form>
        </section>

        <section v-else class="form-section">
          <header class="form-section__header">
            <h3 class="form-section__title">Itinerary created</h3>
            <p class="form-section__text">Start adding Events, Exhibitions, Museums, Galleries, and more.</p>
          </header>
          <RouterLink class="btn border-btn form-section__action" :to="{ name: 'destinations' }"
            >Explore destinations</RouterLink
          >
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDateToISO } from '@/helpers/dateTimeHelper';
import TheCrossIcon from '@/components/icons/TheCrossIcon.vue';
import CreateItineraryFormInput from '@/components/itinerary/CreateItineraryFormInput.vue';
import CreateItineraryFormTextarea from '@/components/itinerary/CreateItineraryFormTextarea.vue';
import CreateItineraryFormSelect from '@/components/itinerary/CreateItineraryFormSelect.vue';
import AppFormDatePicker from '@/components/AppFormDatePicker.vue';
import destinationsQuery from '@/graphql/destination/DestinationsForSelectList.query.gql';
import createItineraryMutation from '@/graphql/itinerary/CreateItinerary.mutation.gql';
import CtaClickedGaEvent from '@/helpers/ga/events/CtaClickedGaEvent';
import { contentTypeParam } from '@/helpers/ga/params/contentTypeParam';
import { sendGaEvent } from '@/helpers/ga/gaSend';
import ItineraryCreatedGaEvent from '@/helpers/ga/events/ItineraryCreatedGaEvent';

export default {
  components: {
    CreateItineraryFormSelect,
    CreateItineraryFormTextarea,
    AppFormDatePicker,
    CreateItineraryFormInput,
    TheCrossIcon,
  },

  data() {
    return {
      form: {
        title: null,
        destination: null,
        description: null,
        dates: [],
      },
      errors: {
        title: null,
        destination: null,
        description: null,
        dates: null,
      },
      isCreating: false,
      finishCreate: false,
      destinationsForSelectList: [],
    };
  },

  apollo: {
    destinationsForSelectList: {
      query: destinationsQuery,
      variables() {
        return {
          picked: false,
        };
      },
    },
  },

  computed: {
    formPayload() {
      const formattedDates = this.form.dates.map((d) => formatDateToISO(d)).filter((d) => d);

      return {
        title: this.form.title,
        destination_id: this.form.destination.id,
        description: this.form.description,
        start_date: formattedDates[0] || null,
        end_date: formattedDates[1] || null,
      };
    },
    isFormSubmitDisabled() {
      const requiredFieldsAreFilled =
        this.form.title &&
        this.form.destination?.id &&
        (this.form.dates.length === 0 || this.form.dates.filter((d) => d).length === 2);

      return !requiredFieldsAreFilled || this.isCreating;
    },
  },

  methods: {
    clearFormErrors() {
      this.errors.title = null;
      this.errors.destination = null;
      this.errors.description = null;
      this.errors.dates = null;
    },

    close() {
      if (this.isCreating) {
        return;
      }

      this.resetForm();
      this.clearFormErrors();
      this.$emit('close');
    },

    resetForm() {
      this.form.title = null;
      this.form.description = null;
      this.form.dates = [];
    },

    validateForm() {
      return (
        this.validateTextInput('title', this.form.title) && this.validateTextInput('description', this.form.description)
      );
    },

    validateTextInput(field, value) {
      if (!value && field === 'title') {
        this.errors[field] = 'Please, enter the title.';

        return false;
      }

      if (!/^[\w\p{L}\-\s'’".,:;/#&()]*$/iu.test(value)) {
        this.errors[field] = 'Invalid format.';

        return false;
      }

      this.errors[field] = null;

      return true;
    },

    handleInput(field) {
      this.errors[field] = null;
    },

    handleSubmit() {
      if (this.isCreating || this.isFormSubmitDisabled || !this.validateForm()) {
        return;
      }

      this.isCreating = true;
      this.clearFormErrors();
      this.$apollo
        .mutate({
          mutation: createItineraryMutation,
          variables: this.formPayload,
        })
        .then(({ data }) => {
          this.isCreating = false;
          this.finishCreate = true;
          this.resetForm();

          const gaEvent = new ItineraryCreatedGaEvent()
            .addItineraryId(data.createItinerary.id)
            .addItineraryName(data.createItinerary.title);

          sendGaEvent(gaEvent);

          this.$emit('created', data.createItinerary);
        })
        .catch((e) => {
          const { graphQLErrors } = e;
          const errorMessage = graphQLErrors[0].message || 'Failed to create your Itinerary.';

          if (graphQLErrors[0].extensions?.validation) {
            const errors = graphQLErrors[0].extensions.validation;

            for (let field in errors) {
              if (this.errors[field] !== undefined) {
                this.errors[field] = errors[field][0];
              }
            }
          }

          this.isCreating = false;
          this.$toast.error(errorMessage);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables-site/variables' as vars;

.app-create-itinerary-modal {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  outline: 0;
  background-color: rgba(vars.$c-total-black, 0.5);

  .itinerary-modal-dialog {
    display: grid;
    grid-template-rows: 1fr;
    height: 100%;

    @media (min-width: vars.$media-640) {
      grid-template-rows: 1fr auto 3fr;
      grid-template-areas:
        'top'
        'content'
        'bottom';
    }
  }

  .itinerary-modal-content {
    --app-agrc-modal-dialog-margin: 12px;
    $formGap: 24px;

    position: relative;
    display: flex;
    flex-direction: column;
    width: calc(100% - var(--app-agrc-modal-dialog-margin) * 2);
    max-width: 740px;
    max-height: calc(100% - var(--app-agrc-modal-dialog-margin) * 2);
    margin: var(--app-agrc-modal-dialog-margin);
    padding: 60px 20px 48px;
    background-color: vars.$c-total-white;
    pointer-events: auto;
    overflow: hidden;

    @media (min-width: vars.$media-640) {
      grid-area: content;
    }

    @media (min-width: vars.$media-800) {
      margin-left: auto;
      margin-right: auto;
    }

    .itinerary-modal-close {
      position: absolute;
      top: 18px;
      right: 8px;
      z-index: 100;
      width: 40px;
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 10px;

      @media (min-width: vars.$media-441) {
        right: 18px;
      }

      @media (min-width: vars.$mobile-break-point + 1px) {
        top: 24px;
        right: 24px;
      }

      svg {
        width: 100%;
        height: 100%;
      }
    }

    .form-section {
      display: flex;
      flex-direction: column;
      gap: $formGap;
      overflow: hidden;
      max-width: 328px;
      width: 100%;
      height: 100%;
      margin: auto;

      &__header {
        flex-shrink: 0;
        text-align: center;
      }

      &__title {
        margin-bottom: 0;
        color: vars.$c-dark-gray;
        font-size: 18px;
        font-weight: 600;
        line-height: normal;
        letter-spacing: 3px;
        text-transform: uppercase;
      }

      &__text {
        margin: 20px 0 0;
        text-align: center;
        font-size: 18px;
        color: vars.$c-dark-5b;

        @media (max-width: vars.$tablet-break-point) {
          font-size: 16px;
        }
      }

      &__action {
        margin-top: 16px;
        padding: 12px 16px;
        font-size: 14px;
        font-weight: 500;
      }
    }

    .modal-form {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: $formGap;
      width: 100%;
      overflow-x: hidden;

      &__btn-submit {
        margin-top: auto;
        padding: 12px 16px;
        border-radius: 1px;
        background-color: vars.$c-dark-gray;
        color: vars.$c-total-white;
        text-align: center;
        font-size: 14px;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 2px;
        text-transform: uppercase;

        &:disabled {
          background-color: vars.$c-gray-a2;
        }

        &:hover,
        &:focus {
          color: vars.$c-total-white !important;
        }

        &:not([disabled]) {
          &:hover {
            opacity: 0.75;
          }
        }

        @media (min-width: vars.$media-640) {
          margin-top: 8px;
        }
      }
    }
  }
}
</style>
